import type React from "react"
import type { ConditionRatingType, RcmCuts } from "types/customer"
import type { CloseAttribute } from './inspectionRouteAttributes'
import { ESS_CLOSE_ATTRIBUTES, SCC_CLOSE_ATTRIBUTES } from './inspectionRouteAttributes'
import { useCustomerData } from "context/customer-provider"
// Lazy: this config is read app-wide (entry chunk), the export forms only in a detail panel.
import {
  LazyEssexConfirmExportComponent,
  LazySurreyConfirmExportComponent,
  LazyMedwayConfirmExportComponent,
  LazyLancashireConfirmExportComponent,
  LazyNorthamptonConfirmExportComponent,
  LazyJerseyHighWorkxExportComponent,
  LazyGloucestershireConfirmExportComponent,
  LazyHertfordshireConfirmExportComponent,
  LazyPeterboroughConfirmExportComponent,
  LazyWestBerkshireConfirmExportComponent,
  LazyBuckinghamshireCausewayExportComponent,
  LazyWestSussexConfirmExportComponent,
  LazyNorthumberlandCausewayExportComponent,
  LazyCambridgeshireAuroraExportComponent
} from "utils/exportComponents/lazy"

/**
 * Customer-specific feature configuration
 * Centralizes all customer customizations in one place
 */
export interface CustomerFeatureConfig {
  // Display configurations
  units: {
    system: 'metric' | 'imperial'
    depthIntervals: number  // 10mm for metric, 0.5" for imperial
    dateFormat: 'US' | 'UK'
  }

  // Feature toggles
  features: {
    showCategories: boolean
    showGenericStatusButtons: boolean
    requiresOfficerCode: boolean
    showEastingNorthings: boolean  // For PDF export
    showOfficerCodeInTopbar: boolean  // Show officer code in app topbar
    hasCustomRiskScore: boolean  // Whether customer has custom risk score calculation
    hasCustomDetectionTypes: boolean  // Whether the customer has a custom defect type list instead of the RR default list
    detectionCounts?: boolean  // Sidecar count badges on Detections V2 (total + per-status/source/type). Default true; flip false to kill-switch.
    requireDeferReason?: boolean  // When true, deferring a defect requires a written reason (e.g. Medway: justify against the intervention level). All customers get the reason box; only these make it mandatory.
    showVerifiedDimensionsBox?: boolean  // Show a "Dimensions verified" checkbox on the defect measurements card (stamps dimensionsVerified/By/At on the cluster).
    allowDefectDimensionsUpdatePostExport?: boolean  // Keep the defect W/L/D editable after the works-system export (still locked mid-export / without an officer code). Nothing re-syncs, so the card notes the works system is not updated.
    allowRepairDimensionsUpdatePostExport?: boolean  // Same as above for the planned-repair W/L.
  }

  // Asset management configuration
  assetManagement: {
    systemName: string | null
    // Lazily loaded (utils/exportComponents/lazy) — render inside a <Suspense>. Truthiness
    // still answers "has an export form" without loading it.
    exportComponent: React.ComponentType<any> | null
    disableOnIntegration: boolean
    defaultExpanded?: boolean
    // What the "nearby Confirm" panels list: Confirm defects (default) or live
    // committed jobs (WSUS). Mirrors api/utils/confirm-resolved.js
    // NEARBY_CONFIRM_SOURCE and only drives wording (tab header, legend,
    // empty states); each row's own `kind` drives links and actions.
    nearbyConfirmSource?: 'defects' | 'jobs'
  }

  // Validation rules
  validation: {
    integrationDisableFields: string[] | 'all'  // List of fields or 'all'
    customTooltips?: {
      noOfficerCode?: string
      inAssetSystem?: string
    }
  }

  // Inspection routes configuration
  inspectionRoutes: {
    enabled: boolean                    // Whether assigned routes view is available
    hasEnquiries: boolean               // Whether to fetch and display enquiries for linking
    closeAttributes: CloseAttribute[]   // Attributes shown when closing an inspection route
    assignedWorkMode?: 'routes' | 'batches'  // What the assigned-work view lists. 'routes' (default) = AssetSurveyTask inspection routes (ESS/SCC). 'batches' = the officer's open inspection batches, worked street by street (MED — its routes are never issued as Connect tasks).
  }

  // Map configuration
  mapSettings: {
    esriApiKey?: string  // For ESRI maps
    apiProvider?: 'je' | null  // For special API providers
    inspectionRoutesFormat: 'line' | 'polygon'  // Format for inspection routes
  }

  // Data fetching configuration
  dataDefaults: {
    excludeCategorisedWrong: boolean
    defaultDaysInterval?: number  // Default days to look back (for RoadCondition page)
    defaultDefectType?: number | string  // Default defect type code or ID
  }

  // Export validation rules - config-driven validation for export components
  exportValidation?: {
    requireInspectionRoute: boolean
    requirePriority: boolean
    requireSorItems: boolean
    requireContract?: boolean
    requireDefaultSorItem?: boolean
    requireCausewayRiskFields?: boolean  // Causeway customers: the component's required Alloy pickers (BUCK: likelihood, consequence, traffic management; NBL: likelihood, consequence, repair type, location type plus the kind-specific field)
    requireOfficerCode?: boolean  // Export-button gate only — unlike features.requiresOfficerCode it doesn't disable editing/annotation
  }

  // Metadata display configuration - which metadata attributes to show and their display names
  metadataAttributes?: Array<{
    metadataKey: string
    displayName: string
  }>

  // When set, the defect detail panel only renders the width/length/depth
  // inputs for defects whose type is in this list (e.g. customer confirm
  // codes flagged as carrying dimensions). When omitted or empty, the panel
  // falls back to the legacy "show whichever dimension is present on the
  // object" behaviour.
  dimensionDefectTypes?: string[]

  // Confirm contract codes offered in the export dialog. Previously stored
  // in DynamoDB (rr-sherlock, confirmDetails.contractOptions), but these are
  // static values that effectively never change, so they live in code
  // instead. If we find ourselves changing them often, we can move them back
  // to database storage.
  contractOptions?: string[]
}

/**
 * Customer configurations indexed by customer ID
 */
const customerConfigs: Record<string, CustomerFeatureConfig> = {
  // Essex - Confirm with officer code requirement
  ESS: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,  // Has Confirm export component
      requiresOfficerCode: true,  // Special ESS requirement
      showEastingNorthings: false,
      showOfficerCodeInTopbar: true,  // Show officer code in topbar
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true
    },
    assetManagement: {
      systemName: 'Confirm',
      exportComponent: LazyEssexConfirmExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        noOfficerCode: 'Cannot be edited because officer code is not configured.',
        inAssetSystem: 'Cannot be edited because defect is already in Confirm.'
      }
    },
    inspectionRoutes: {
      enabled: true,
      hasEnquiries: true,
      closeAttributes: ESS_CLOSE_ATTRIBUTES,
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDefectType: 'CWDF',
    },
    exportValidation: {
      requireInspectionRoute: true,
      // The priority comes from consequence x likelihood; a 0 score is sent as
      // "N/A", which Confirm rejects ("N/A is not a valid priority code").
      requirePriority: true,
      requireSorItems: false,
    },
    // Sourced from the "Transfer dimensions? = YES" rows in the Essex-supplied
    // defect-code spreadsheet — types worth capturing width/length/depth for.
    dimensionDefectTypes: [
      "road_surface_defect",
      "CWDF",
      "FWDF",
      "HWSW",
      "DEB",
      "KBDM",
      "KBMA",
      "HWKC",
      "KBMS",
      "OFW",
      "OGVE",
      "OGRO",
      "GRDM",
      "GRMS",
      "RSDM",
      "SCDM",
      "SCMA",
      "SCMS",
      "VRGE",
      "VGOR",
      "BDCH",
    ],
  },

  // Surrey - Confirm with SOR items + priority requirement
  SCC: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: true,  // Show officer code in topbar
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true
    },
    assetManagement: {
      systemName: 'Confirm',
      exportComponent: LazySurreyConfirmExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Confirm.'
      }
    },
    inspectionRoutes: {
      enabled: true,
      hasEnquiries: false,
      closeAttributes: SCC_CLOSE_ATTRIBUTES,
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDefectType: 'HW06',
    },
    exportValidation: {
      requireInspectionRoute: true,
      requirePriority: true,
      requireSorItems: true,
      // The export API rejects a user with no officer code (every Temporal
      // defect-export workflow requires OfficerCode), so gate the button
      // instead of failing on click.
      requireOfficerCode: true,
    },
    metadataAttributes: [
      { metadataKey: 'Planned_Wo', displayName: 'Planned works' },
      { metadataKey: 'Planned_Ye', displayName: 'Planned year' },
      { metadataKey: 'Completed', displayName: 'Completed date' },
      { metadataKey: 'P_Status', displayName: 'Planned works status' },
    ],
  },

  // Medway - Confirm
  MED: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true,
      requireDeferReason: true  // Medway mandates a reason on defer (intervention-level justification for the inspection record)
    },
    assetManagement: {
      systemName: 'Confirm',
      exportComponent: LazyMedwayConfirmExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Confirm.'
      }
    },
    inspectionRoutes: {
      enabled: true,
      hasEnquiries: true,
      closeAttributes: [],
      assignedWorkMode: 'batches',  // MED: open inspection batches, worked street by street
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDefectType: 'HPOT'
    },
    exportValidation: {
      requireInspectionRoute: true,
      requirePriority: true,
      requireSorItems: false,
      requireDefaultSorItem: true,
      // The export API rejects a user with no officer code (every Temporal
      // defect-export workflow requires OfficerCode), so gate the button
      // instead of failing on click.
      requireOfficerCode: true,
    },
    contractOptions: ['HIC17', 'MSS12']
  },
  GLOU: {
    units: {
      system: 'metric',
      depthIntervals: 5,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true
    },
    assetManagement: {
      systemName: 'Confirm',
      exportComponent: LazyGloucestershireConfirmExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Confirm.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDefectType: 'H001'
    },
    exportValidation: {
      requireInspectionRoute: true,
      requirePriority: true,
      requireSorItems: true,
      requireContract: true,
      // The export API rejects a user with no officer code (every Temporal
      // defect-export workflow requires OfficerCode), so gate the button
      // instead of failing on click.
      requireOfficerCode: true,
    }
  },
  // Cambridgeshire use Symology Aurora, not Confirm. Detections are raised as
  // Aurora *Cases* — reports for the council to triage into a defect or
  // dismiss — so there is no priority, SOR item or contract to require here;
  // Cambridgeshire assign those themselves at triage.
  CAM: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: 'Aurora',
      exportComponent: LazyCambridgeshireAuroraExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because a case has already been raised in Aurora.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    },
    exportValidation: {
      // Aurora cases key on the USRN, not a Route Reports inspection route,
      // and carry none of the Confirm-style selections.
      requireInspectionRoute: false,
      requirePriority: false,
      requireSorItems: false,
      requireContract: false,
    }
  },
  PET: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true
    },
    assetManagement: {
      systemName: 'Confirm',
      exportComponent: LazyPeterboroughConfirmExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Confirm.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDefectType: '1SPH'
    },
    exportValidation: {
      requireInspectionRoute: true,
      requirePriority: true,
      requireSorItems: false,
      requireContract: true,
      // The export API rejects a user with no officer code (every Temporal
      // defect-export workflow requires OfficerCode), so gate the button
      // instead of failing on click.
      requireOfficerCode: true,
    }
  },
  HERT: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true
    },
    assetManagement: {
      systemName: 'Confirm',
      exportComponent: LazyHertfordshireConfirmExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Confirm.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDefectType: 'CPH6'
    },
    exportValidation: {
      requireInspectionRoute: true,
      requirePriority: true,
      requireSorItems: false,
      // The export API rejects a user with no officer code (every Temporal
      // defect-export workflow requires OfficerCode), so gate the button
      // instead of failing on click.
      requireOfficerCode: true,
    }
  },

  // Buckinghamshire - Causeway AQS integration
  BUCK: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: 'Causeway',
      exportComponent: LazyBuckinghamshireCausewayExportComponent,
      disableOnIntegration: true,
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Causeway.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    },
    exportValidation: {
      requireInspectionRoute: false,
      requirePriority: false,
      requireSorItems: false,
      requireCausewayRiskFields: true,  // Gates export button until likelihood, consequence, severity, and traffic management are all set
      // The export API rejects a user with no officer code (every Temporal
      // defect-export workflow requires OfficerCode), so gate the button
      // instead of failing on click.
      requireOfficerCode: true,
    }
  },

  // Lancashire - Confirm (migrated from Symology)
  LAN: {
    units: {
      system: 'metric',
      depthIntervals: 5,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: true,  // Special PDF feature for LAN
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: true,  // LAN has custom risk score calculation
      hasCustomDetectionTypes: true,
      showVerifiedDimensionsBox: true,  // Inspectors tick once they've checked the dims on site (also after the Confirm export)
      allowDefectDimensionsUpdatePostExport: true,  // ...and can correct the defect dims they found on site after the Confirm export
      allowRepairDimensionsUpdatePostExport: false,  // Repair dims stay locked once sent to Confirm
    },
    assetManagement: {
      systemName: 'Confirm',
      exportComponent: LazyLancashireConfirmExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Confirm.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDefectType: 'H019'
    },
    exportValidation: {
      requireInspectionRoute: true,
      requirePriority: true,
      requireContract: true,
      requireSorItems: false,
      // LAN's Confirm workflow sends the user's officer code as LoggedBy with no
      // fallback, so exports without one fail asynchronously in Confirm.
      requireOfficerCode: true,
    }
  },

  // Northampton - Works Manager (Confirm)
  NOR: {
    units: {
      system: 'metric',
      depthIntervals: 5,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true
    },
    assetManagement: {
      systemName: 'Works Manager',
      exportComponent: LazyNorthamptonConfirmExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Works Manager.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDefectType: 2447
    },
    exportValidation: {
      requireInspectionRoute: true,
      requirePriority: true,
      requireSorItems: false,
      // The Works Manager Temporal workflow sends the user's officer code as the
      // inspector with no fallback, so exports without one fail asynchronously.
      requireOfficerCode: true,
    }
  },

  // Jersey - HighwayWorkx
  JER: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true
    },
    assetManagement: {
      systemName: 'HighwayWorkx',
      exportComponent: LazyJerseyHighWorkxExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in HighwayWorkx.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      apiProvider: 'je',  // Special API provider for Jersey
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDefectType: 'Potholes'
    },
    exportValidation: {
      requireInspectionRoute: true,
      requirePriority: true,
      requireSorItems: false,
      // The HighwayWorkx Temporal workflow sends the user's officer code as the
      // InspectorName with no fallback, so exports without one fail asynchronously.
      requireOfficerCode: true,
    }
  },

  // ABDS - Special customer with no categories or status buttons
  ABDS: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Special: no categories for ABDS
      showGenericStatusButtons: false,  // Special: no status buttons for ABDS
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: true,  // ABDS has custom risk score calculation
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],  // No fields disabled for ABDS
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  // PIC - Customer with special map style
  PIC: {
    units: {
      system: 'metric',  // May need adjustment based on actual customer data
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in asset management system.'
      }
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  // DFI - Special confidence values
  DFI: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  // ROADS - Special confidence values and longer default interval
  ROADS: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDaysInterval: 120  // 120 days for ROADS vs default 7
    }
  },

  // ANDY - Low confidence values
  ANDY: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  // LGW (Gatwick) - Low confidence values
  LGW: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  // STOC - Low confidence values
  STOC: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  // WSUS - West Sussex (Confirm)
  WSUS: {
    units: {
      system: 'metric',
      // 5 mm, like LAN: WSCC's default pothole view starts at 35 mm
      // (config.customer.detection_min_depth; ART-3227), which 10 mm slider
      // steps and depth buckets can't express.
      depthIntervals: 5,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      // OfficerCodeGate in the export component already locks the whole
      // exporter when the user has no officer code, so this stays false to
      // avoid a second, differently-worded gate (matches HERT).
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true
    },
    assetManagement: {
      systemName: 'Confirm',
      exportComponent: LazyWestSussexConfirmExportComponent,
      disableOnIntegration: true,
      // Richard (WSCC, Oct 2026): show committed, unfinished, uncancelled
      // jobs on the map rather than defects (ART-3227).
      nearbyConfirmSource: 'jobs'
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Confirm.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      // CW01 "CW: Carriageway Pothole" — the type WSCC uses on every
      // reference pothole defect they gave us.
      defaultDefectType: 'CW01'
    },
    exportValidation: {
      // The exporter resolves the Confirm feature from the matched
      // inspection route (inspection_id minus its "ID" prefix is the
      // centralAssetId), so an unmatched detection cannot be exported.
      requireInspectionRoute: true,
      requirePriority: true,
      requireSorItems: false,
      // The export API rejects a user with no officer code (every Temporal
      // defect-export workflow requires OfficerCode), so gate the button
      // instead of failing on click.
      requireOfficerCode: true,
    }
  },

  // NBL - Northumberland - Causeway (Alloy). NCC file each kind of defect on
  // its own Alloy design and their workflows raise the job, so the export
  // component sends a defect, not a job (see utils/exportComponents/Northumberland).
  NBL: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      // Drawing types come from config.customer_defect_type (codes are the RR
      // type strings plus gully_defect, which the RR default list lacks).
      hasCustomDetectionTypes: true
    },
    assetManagement: {
      systemName: 'Causeway',
      exportComponent: LazyNorthumberlandCausewayExportComponent,
      disableOnIntegration: true,
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Causeway.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    },
    exportValidation: {
      requireInspectionRoute: false,
      requirePriority: false,
      requireSorItems: false,
      requireCausewayRiskFields: true,
      // The export API rejects a user with no officer code (every Temporal
      // defect-export workflow requires OfficerCode), so gate the button
      // instead of failing on click.
      requireOfficerCode: true,
    }
  },

  // EKFB - HS2 main works contractor (Eiffage Kier Ferrovial BAM). Exports to
  // EKFB's own Works Manager tenant with the same flow as NOR (reuses the
  // Northampton export component; the Temporal workflow is EKFB's own).
  EKFB: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,  // Hidden when export component exists
      showGenericStatusButtons: false,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: true  // Works Manager defect types via GetDefectTypes_V1_0
    },
    assetManagement: {
      systemName: 'Works Manager',
      exportComponent: LazyNorthamptonConfirmExportComponent,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in Works Manager.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
      // defaultDefectType: EKFB's Works Manager defect type code is not yet
      // known (NOR uses 2447); set once EKFB confirm it, together with
      // config.customer.default_defect_type.
    },
    exportValidation: {
      requireInspectionRoute: true,
      requirePriority: true,
      requireSorItems: false,
      // The Works Manager Temporal workflow sends the user's officer code as the
      // inspector with no fallback, so exports without one fail asynchronously.
      requireOfficerCode: true,
    }
  },

  // AGB - Argyll and Bute. No asset-management integration yet; the UKPMS
  // PMS_Network export is the route set (cloud-pipeline rrgpu_inspection_routes.agb).
  AGB: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  // EGKK - Low confidence values
  EGKK: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  DVN: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  WBRK: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false,
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: LazyWestBerkshireConfirmExportComponent,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  ILN: {
    units: {
      system: 'imperial',
      depthIntervals: 0.5,
      dateFormat: 'US'
    },
    features: {
      showCategories: false,
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    },
    metadataAttributes: [
      {
        metadataKey: 'mp',
        displayName: 'Milepost'
      },
      {
        metadataKey: 'spjoin_route',
        displayName: 'Route'
      },
      {
        metadataKey: 'spjoin_msection',
        displayName: 'M-Section'
      },
      {
        metadataKey: 'spjoin_rampid',
        displayName: 'Ramp ID'
      },
      {
        metadataKey: 'spjoin_direction',
        displayName: 'Direction'
      },
      {
        metadataKey: 'spjoin_interchange',
        displayName: 'Interchange'
      }
    ]
  },

  NH: {
    units: {
      system: 'imperial',
      depthIntervals: 0.5,
      dateFormat: 'US'
    },
    features: {
      showCategories: false,
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: false
    },
    validation: {
      integrationDisableFields: [],
      customTooltips: {}
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true
    }
  },

  // MKE = Milwaukee (US) in this deployment. Units are set imperial STATICALLY
  // (like ILN) — the customerData.american override in useCustomerConfig only
  // reaches hook-based surfaces, and the unauthenticated magic-link pages
  // resolve config without customerData, so they'd fall back to metric.
  MKE: {
    units: {
      system: 'imperial',
      depthIntervals: 0.5,
      dateFormat: 'US'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in asset management system.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDaysInterval: 7
    }
  },

  // CFX (Central Florida Expressway, US) — same reasoning as MKE: imperial
  // must be static so the unauthenticated magic-link pages get the right
  // units. Everything else mirrors DEFAULT.
  CFX: {
    units: {
      system: 'imperial',
      depthIntervals: 0.5,
      dateFormat: 'US'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in asset management system.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDaysInterval: 7
    }
  },

  // ESUS (East Sussex) — defect depth is recorded in 5mm steps rather than the
  // default 10mm. Everything else mirrors DEFAULT.
  ESUS: {
    units: {
      system: 'metric',
      depthIntervals: 5,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in asset management system.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDaysInterval: 7
    }
  },

  DEFAULT: {
    units: {
      system: 'metric',
      depthIntervals: 10,
      dateFormat: 'UK'
    },
    features: {
      showCategories: false, // C1–C4 retired Aug 2026 — write-only in practice (no filter/search consumer; only ILN ever used them, via the legacy page). See the defect-lifecycle definition (kept outside the repo).
      showGenericStatusButtons: true,  // Show generic buttons when no export component
      requiresOfficerCode: false,
      showEastingNorthings: false,
      showOfficerCodeInTopbar: false,
      hasCustomRiskScore: false,
      hasCustomDetectionTypes: false
    },
    assetManagement: {
      systemName: null,
      exportComponent: null,
      disableOnIntegration: true
    },
    validation: {
      integrationDisableFields: 'all',
      customTooltips: {
        inAssetSystem: 'Cannot be edited because defect is already in asset management system.'
      }
    },
    inspectionRoutes: {
      enabled: false,
      hasEnquiries: false,
      closeAttributes: [],
    },
    mapSettings: {
      inspectionRoutesFormat: 'line'
    },
    dataDefaults: {
      excludeCategorisedWrong: true,
      defaultDaysInterval: 7  // Default to 7 days for most customers
    }
  }
}

/**
 * Get customer configuration by ID
 * Falls back to DEFAULT if customer not found
 */
export const getCustomerConfig = (customerId: string | undefined): CustomerFeatureConfig => {
  if (!customerId) return customerConfigs.DEFAULT
  return customerConfigs[customerId] || customerConfigs.DEFAULT
}

/**
 * Whether the customer has an explicit entry here (vs falling back to
 * DEFAULT). Lets the internal Customers page distinguish "configured in code"
 * from "inheriting defaults".
 */
export const hasExplicitCustomerConfig = (customerId: string | undefined): boolean =>
  !!customerId && customerId in customerConfigs

/**
 * Hook to use customer configuration in React components
 */
export const useCustomerConfig = (customerId: string | undefined): CustomerFeatureConfig => {
  const { customerData } = useCustomerData()
  return getCustomerConfigWithFallback(customerId, customerData)
}

/**
 * Per-customer road condition rating scale overrides.
 * The stored/served condition value is always the raw 0-100 score; the rating type
 * only determines the display conversion. Customers not listed here fall back to
 * "pci" (american) or "rcm" (everyone else).
 * Server-side twin: api/utils/condition-rating.js (CSV/shapefile exports) — keep in sync.
 */
const conditionRatingTypes: Partial<Record<string, ConditionRatingType>> = {
  CFX: 'pcs',
}

export const getConditionRatingType = (customerId: string | undefined, american: boolean | undefined): ConditionRatingType => {
  return conditionRatingTypes[customerId ?? ''] ?? (american ? 'pci' : 'rcm')
}

/**
 * Hook to resolve the road condition rating scale for the active customer
 */
export const useConditionRatingType = (): ConditionRatingType => {
  const { customerId, customerData } = useCustomerData()
  return getConditionRatingType(customerId, customerData?.american)
}

/**
 * Hook to resolve the active customer's RCM cut points (its condition
 * calibration, config.condition_calibration); undefined until it is known.
 */
export const useRcmCuts = (): RcmCuts | undefined => {
  const { customerData } = useCustomerData()
  return customerData?.conditionCalibration?.rcmCuts
}

/**
 * Check if a field should be disabled based on integration status
 */
export const shouldDisableField = (
  config: CustomerFeatureConfig,
  fieldName: string,
  hasConfirmId: boolean,
  missingOfficerCode: boolean
): boolean => {
  if (!config.assetManagement.disableOnIntegration) return false

  const disableAll = config.validation.integrationDisableFields === 'all'
  const disableSpecific = Array.isArray(config.validation.integrationDisableFields) &&
    config.validation.integrationDisableFields.includes(fieldName)

  return (disableAll || disableSpecific) && (hasConfirmId || missingOfficerCode)
}

/**
 * Get the appropriate tooltip message for a disabled field
 */
export const getDisabledTooltip = (
  config: CustomerFeatureConfig,
  hasConfirmId: boolean,
  missingOfficerCode: boolean
): string => {
  if (hasConfirmId) {
    return config.validation.customTooltips?.inAssetSystem || ''
  }
  if (missingOfficerCode) {
    return config.validation.customTooltips?.noOfficerCode || ''
  }
  return ''
}

/**
 * Export all customer IDs for reference
 */
export const CUSTOMER_IDS = {
  ESSEX: 'ESS',
  SURREY: 'SCC',
  MEDWAY: 'MED',
  LANCASHIRE: 'LAN',
  NORTHAMPTON: 'NOR',
  JERSEY: 'JER',
  ABDS: 'ABDS',
  PIC: 'PIC',
  DFI: 'DFI',
  ROADS: 'ROADS',
  ANDY: 'ANDY',
  LGW: 'LGW',
  EGKK: 'EGKK',
  BUCK: 'BUCK',
  ILN: 'ILN',
  NH: 'NH',
} as const

/**
 * Helper function to get configuration with fallback to customerData
 * This provides backward compatibility during migration
 */
export const getCustomerConfigWithFallback = (
  customerId: string | undefined,
  customerData?: { american?: boolean }
): CustomerFeatureConfig => {
  const config = getCustomerConfig(customerId)

  // If customer data indicates American units but config doesn't, override
  if (customerData?.american && config.units.system !== 'imperial') {
    return {
      ...config,
      units: {
        ...config.units,
        system: 'imperial',
        depthIntervals: 0.5,
        dateFormat: 'US'
      }
    }
  }

  return config
}

/**
 * Check if customer requires officer code
 */
export const customerRequiresOfficerCode = (customerId: string | undefined): boolean => {
  const config = getCustomerConfig(customerId)
  return config.features.requiresOfficerCode
}

/**
 * Get default confidence values for customer
 */
// Basemap tone per customer — the ONE knob the DV2 map paints derive from (network colour,
// emphasis ring, dot stroke). Custom basemaps: ESS runs a light vector style; ILN/CFX satellite
// imagery. Everything else uses the default dark vector style. Extend as customers get custom
// styles — tune here, never per-layer.
export type BasemapTone = "dark" | "light" | "satellite" | "noir"
const BASEMAP_TONES: Record<string, BasemapTone> = {
  // ESS runs a custom OS style: near-BLACK ground with bright cyan roads baked in — "noir".
  // Teal overlays drown among the basemap's own roads and dark pills vanish, so noir inverts:
  // pale glowing network, light-material clusters with dark counts.
  ESS: "noir",
  // WSUS runs the stock OS Light stylesheet (their own OS Data Hub key).
  WSUS: "light",
  ILN: "satellite",
  CFX: "satellite",
}
export const getBasemapTone = (customerId: string | undefined): BasemapTone =>
  (customerId && BASEMAP_TONES[customerId]) || "dark"

// ART-3048: ONE detector-confidence floor for every customer. Per-customer
// values (ESS 60, SCC 10, NH 35, …) were the same class of read-time tuning
// knob as the retired modelator thresholds — the coverage mode is the noise
// control now (High-confidence trusts the modelator verdict; Full coverage
// means full), so the floor's only job is dropping pure garbage boxes, which
// is not customer-specific. Single place to change, applies everywhere.
export const UNIFIED_DETECTOR_CONFIDENCE_MIN = 30 // advisory only — the API branch owns the floors (api/utils/coverage.js)

export const getDefaultConfidence = (customerId: string | undefined) => {
  return {
    confidence: UNIFIED_DETECTOR_CONFIDENCE_MIN,
    // ART-3048: the per-customer modelator confidence threshold is retired.
    // The High-confidence / Full-coverage toggle trusts the modelator's binary
    // has_pothole verdict at the model's own operating point; trimming its
    // low-confidence affirmations with per-customer magic numbers second-guessed
    // the model and silently broke when the model (and its confidence scale)
    // was swapped (see ART-3023). Precision tuning belongs in the pipeline's
    // operating point (epic ART-3010 child C), not read-time config.
    modelator: 0,
  }
}

export const getDefaultExcludeCategorisedWrong = (customerId: string | undefined) => {
  const config = getCustomerConfig(customerId)
  return config.dataDefaults.excludeCategorisedWrong
}

/**
 * Does this customer hand repairs off to a works management system?
 *
 * The honest test is "has a named system AND no generic status buttons" — the
 * one StatusQuickFilters already applies. Not `hasAssetManagementSystem`, which
 * is a bare `!!exportComponent` and so counts WBRK, whose export component
 * contains no export button at all. WBRK and ABDS are package-only in practice
 * and must come out of this false.
 *
 * Where this is true the works system owns the defect once it is exported, so RR
 * does not offer a manual Resolve (Slack, 18 Aug: at export the responsibility
 * hands off, and the button only clutters). Where it is false RR *is* the works
 * system — the assignment framework records completion and Resolved is
 * meaningful here.
 */
export const configUsesWorksManagementSystem = (config: CustomerFeatureConfig) =>
  !config.features.showGenericStatusButtons && !!config.assetManagement.systemName

export const usesWorksManagementSystem = (customerId: string | undefined) =>
  configUsesWorksManagementSystem(getCustomerConfig(customerId))

/**
 * Check if officer code should be shown in topbar
 */
export const shouldShowOfficerCodeInTopbar = (customerId: string | undefined): boolean => {
  const config = getCustomerConfig(customerId)
  return config.features.showOfficerCodeInTopbar
}
